import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { MessageService } from 'primeng/api';
import { RequisitiService } from '../../../services/requisiti.service';
import { ClienteModel } from '../../../core/models/api/cliente.model';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-cliente-dialog',
  standalone: false,
  templateUrl: './create-cliente-dialog.component.html',
  styleUrl: './create-cliente-dialog.component.scss'
})
export class CreateClienteDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() clienti: ClienteModel[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() clienteCreated = new EventEmitter<void>();

  ragioneSociale: string = '';
  tipologiaCliente: 'PUBBLICO' | 'PRIVATO' | null = null;
  saving: boolean = false;

  tipologieOptions = [
    { label: 'Pubblico', value: 'PUBBLICO', icon: 'account_balance' },
    { label: 'Privato', value: 'PRIVATO', icon: 'business' }
  ];

  constructor(
    private requisitiService: RequisitiService,
    private messageService: MessageService,
    private logger: NGXLogger
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      // Reset del form quando il dialog viene aperto
      this.resetForm();
    }
  }

  /**
   * Verifica se esiste già un cliente con la stessa ragione sociale (case-insensitive)
   */
  clienteEsiste(): boolean {
    if (!this.ragioneSociale.trim()) {
      return false;
    }

    const ragioneSocialeNormalized = this.ragioneSociale.trim().toLowerCase();
    return this.clienti.some(
      cliente => cliente.ragioneSociale.toLowerCase() === ragioneSocialeNormalized
    );
  }

  /**
   * Verifica se il form è valido
   */
  isFormValid(): boolean {
    return !!(
      this.ragioneSociale.trim() &&
      this.tipologiaCliente &&
      !this.clienteEsiste()
    );
  }

  /**
   * Salva il nuovo cliente
   */
  async saveCliente(): Promise<void> {
    if (!this.isFormValid()) {
      if (this.clienteEsiste()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore Validazione',
          detail: 'Esiste già un cliente con questa ragione sociale',
          life: 5000
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore Validazione',
          detail: 'Compila tutti i campi obbligatori',
          life: 5000
        });
      }
      return;
    }

    this.saving = true;

    try {
      const nuovoCliente: Omit<ClienteModel, 'id'> = {
        ragioneSociale: this.ragioneSociale.trim(),
        tipologiaCliente: this.tipologiaCliente!,
        numeroReferenze: 0 // Inizialmente nessuna referenza
      };

      await firstValueFrom(this.requisitiService.createCliente(nuovoCliente));

      this.logger.info('[CREATE-CLIENTE-DIALOG] Cliente creato con successo');

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Cliente creato con successo',
        life: 3000
      });

      this.clienteCreated.emit();
      this.closeDialog();
    } catch (error) {
      this.logger.error('[CREATE-CLIENTE-DIALOG] Errore durante la creazione del cliente:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile creare il cliente. Riprova più tardi.',
        life: 5000
      });
    } finally {
      this.saving = false;
    }
  }

  /**
   * Chiude il dialog
   */
  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  /**
   * Reset del form
   */
  private resetForm(): void {
    this.ragioneSociale = '';
    this.tipologiaCliente = null;
    this.saving = false;
  }
}
